import { fabric } from 'fabric';
import PicabooConstants, { IMAGE_TYPES } from '../common/PicabooConstants';
import { getContainingRectangle, intersects } from './geometry';
import FabricUtils from '../utils/FabricUtils';
import { fromJS } from 'immutable';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

let nodeRender = false;
if (typeof window === 'undefined') {
  nodeRender = true;
}

export function createCanvas() {
  let canvas = fabric.util.createCanvasElement();
  return canvas;
}

const DPI = 300;

export class GraphicItemUtils {
  static getCanvas = createCanvas;
  /**
   * Determines what the width and height of an image should be so that it fills the entire well
   * without changing the aspect ratio.
   * @param imageWidth - original image width
   * @param imageHeight - original image height
   * @param wellWidth
   * @param wellHeight
   * @returns {{width: *, height: number}}
   */
  static fitImage(
    imageWidth,
    imageHeight,
    wellWidth,
    wellHeight,
    image = undefined,
  ) {
    //ratio = height/width
    //height = ratio*width
    //width = height/ratio

    if (image) {
      if (!imageWidth) {
        imageWidth = image.get('width');
      }
      if (!imageHeight) {
        imageHeight = image.get('height');
      }
    }

    let ratio = imageHeight / imageWidth;

    if (
      image != undefined &&
      (image.get('orientation') == PicabooConstants.IMAGE_ORIENTATION.LEFT ||
        image.get('orientation') == PicabooConstants.IMAGE_ORIENTATION.RIGHT) &&
      Number(image.get('width')) > Number(image.get('height'))
    ) {
      ratio = imageWidth / imageHeight;
    }

    let newWidth = wellWidth;
    let newHeight = ratio * newWidth;

    //Enlarge if necessary
    if (newHeight < wellHeight) {
      newHeight = wellHeight;
      newWidth = newHeight / ratio;
    }

    return { width: newWidth, height: newHeight };
  }

  static toColor(num, forceAlpha = undefined) {
    if (typeof num === 'string' || num instanceof String) {
      if (num[0] == '#') num = num.replace('#', '0x');
      num = parseInt(num);
    }

    num >>>= 0;
    var b = num & 0xff,
      g = (num & 0xff00) >>> 8,
      r = (num & 0xff0000) >>> 16,
      a = ((num & 0xff000000) >>> 24) / 255;
    if (forceAlpha != undefined) {
      a = forceAlpha;
    } else if (a === 0) {
      a = 1;
    }
    return 'rgba(' + [r, g, b, a].join(',') + ')';
  }

  static shouldApplyFilter(pi, filterName) {
    if (pi === undefined) {
      return false;
    }

    const filters = pi.has('filters') ? pi.get('filters') : fromJS([]);

    let mirrorIndex = 0;

    const mirrorFilter = filters.find((filter, index) => {
      let isMirror = filter.get('name') == filterName;
      if (isMirror) {
        mirrorIndex = index;
      }
      return isMirror;
    });
    return mirrorFilter ? mirrorFilter.get('value') === 1 : false;
  }

  static getURLForDimensions(
    projectImage,
    imageWidth,
    imageHeight,
    imagePathValue,
  ) {
    if (imagePathValue === PicabooConstants.IMAGE_RESOLUTIONS.ORIGINAL) {
      let turl;
      if (projectImage.get(PicabooConstants.IMAGE_RESOLUTIONS.ORIGINAL)) {
        turl = projectImage.get(PicabooConstants.IMAGE_RESOLUTIONS.ORIGINAL);
      } else {
        turl = projectImage.get('path');
      }
      const url = turl;
      const fallbackUrl =
        projectImage.get(PicabooConstants.IMAGE_RESOLUTIONS.HIGH) ?? null;
      if (url) return { url, fallbackUrl };
    }

    if (imagePathValue === PicabooConstants.IMAGE_RESOLUTIONS.THUMB) {
      const url = projectImage.get(PicabooConstants.IMAGE_RESOLUTIONS.THUMB);
      if (url) return { url };
    }

    // MB-33: Improve Resolution in Project Preview
    // Use high resolution image whenever it exists
    const url = projectImage
      ? projectImage.get(PicabooConstants.IMAGE_RESOLUTIONS.HIGH)
      : null;
    return { url };
  }

  static resizeImage(
    src,
    targetWidth,
    targetHeight,
    positionX,
    positionY,
    destWidth,
    destHeight,
    projectImage,
    rotation = 0,
    mirrorHorizontal = false,
    mirrorVertical = false,
    image_orientation = 1,
  ) {
    targetWidth = Math.max(targetWidth, 1);
    targetHeight = Math.max(targetHeight, 1);

    if (
      projectImage &&
      projectImage.get('orientation') ==
        PicabooConstants.IMAGE_ORIENTATION.UPSIDE_DOWN &&
      image_orientation == PicabooConstants.IMAGE_ORIENTATION.UPSIDE_DOWN
    ) {
      rotation += 180;
    }

    if (
      projectImage &&
      (projectImage.get('orientation') ==
        PicabooConstants.IMAGE_ORIENTATION.LEFT ||
        projectImage.get('orientation') ==
          PicabooConstants.IMAGE_ORIENTATION.RIGHT)
    ) {
      if (image_orientation != 1) {
        rotation +=
          image_orientation == PicabooConstants.IMAGE_ORIENTATION.RIGHT
            ? 270
            : 90;
      }

      if (
        Number(projectImage.get('width')) > Number(projectImage.get('height'))
      ) {
        var temp = targetWidth;
        targetWidth = targetHeight;
        targetHeight = temp;
        temp = positionX;
        positionX = positionY;
        positionY = temp;
        temp = destWidth;
        destWidth = destHeight;
        destHeight = temp;
      }
    }

    if (rotation >= 360) {
      rotation -= 360;
    }

    src = this.rotateCanvas(
      this.mirrorCanvas(src, mirrorHorizontal, mirrorVertical),
      rotation,
    );

    // 2019-03-21 - I changed this to crop first, then resize. This was causing images that were zoomed in a lot to not render. Also, this should theoretically help performance.
    // for more info, see https://stackoverflow.com/questions/6081483/maximum-size-of-a-canvas-element and http://teampulse.picaboo.com/view#item/15716
    var imageDiff = src.width / targetWidth;

    var srcRatio = (src.width / src.height).toFixed(13);
    var targetRatio = (destWidth / destHeight).toFixed(13);

    var context = null;
    const imageCanvas = createCanvas();
    imageCanvas.width = destWidth / window.devicePixelRatio;
    imageCanvas.height = destHeight / window.devicePixelRatio;

    positionX = positionX / window.devicePixelRatio;
    positionY = positionY / window.devicePixelRatio;
    if (
      targetWidth > 1 &&
      (srcRatio != targetRatio || positionX != 0 || positionY != 0)
    ) {
      let fabricCanvas = new fabric.Canvas(imageCanvas, {
        enableRetinaScaling: false,
      });
      let fabricImage = new fabric.Image(src, {
        left: positionX,
        top: positionY,
      });

      // if (!global.isRenderer && imageDiff > 2) {
      //   let filter = new fabric.Image.filters.Resize();
      //   filter.scaleX = 1 / (imageDiff / window.devicePixelRatio);
      //   filter.scaleY = 1 / (imageDiff / window.devicePixelRatio);
      //   filter.resizeType = 'bilinear';
      //   fabricImage.filters.push(filter);
      //   fabricImage.applyFilters();
      // }

      fabricImage.scale(1 / (imageDiff * window.devicePixelRatio));
      fabricCanvas.add(fabricImage);
      fabricCanvas.renderAll();
    } else {
      context = imageCanvas.getContext('2d');
      context.drawImage(src, 0, 0, Math.min(destWidth), Math.min(destHeight));
    }
    return imageCanvas;
  }

  static rotateCanvas(src, degrees) {
    if (degrees != 0) {
      const rotateCanvas = createCanvas();
      const context = rotateCanvas.getContext('2d');
      switch (degrees) {
        case 90:
          rotateCanvas.setAttribute('width', src.height);
          rotateCanvas.setAttribute('height', src.width);
          context.rotate((degrees * Math.PI) / 180);
          context.drawImage(src, 0, -src.height);
          break;
        case 180:
          rotateCanvas.setAttribute('width', src.width);
          rotateCanvas.setAttribute('height', src.height);
          context.rotate((degrees * Math.PI) / 180);
          context.drawImage(src, -src.width, -src.height);
          break;
        case 270:
          rotateCanvas.setAttribute('width', src.height);
          rotateCanvas.setAttribute('height', src.width);
          context.rotate((degrees * Math.PI) / 180);
          context.drawImage(src, -src.width, 0);
          break;
      }

      return rotateCanvas;
    }
    return src;
  }

  static mirrorCanvas(src, horizontal, vertical) {
    if (horizontal || vertical) {
      let xScale = horizontal ? -1 : 1;
      let yScale = vertical ? -1 : 1;

      const imageCanvas = createCanvas();
      imageCanvas.width = src.width;
      imageCanvas.height = src.height;

      const context = imageCanvas.getContext('2d');
      context.scale(xScale, yScale);
      context.drawImage(
        src,
        horizontal ? -src.width : 0,
        vertical ? -src.height : 0,
      );

      return imageCanvas;
    }

    return src;
  }

  static cropPlusExport(img, cropX, cropY, cropWidth, cropHeight, dim) {
    var canvas1 = createCanvas();
    // let cornerImageWidth = cropWidth * .001 * dim.width;
    // let cornerImageHeight = cropHeight * .001 * dim.width;
    let cornerImageWidth = cropWidth * 0.001 * dim.ratioWidth;
    let cornerImageHeight = cropHeight * 0.001 * dim.ratioWidth;

    canvas1.width = cornerImageWidth;
    canvas1.height = cornerImageHeight;
    var ctx1 = canvas1.getContext('2d');
    // use the extended from of drawImage to draw the
    // cropped area to the temp canvas
    ctx1.drawImage(
      img,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cornerImageWidth,
      cornerImageHeight,
    );

    return canvas1;
  }

  static loadImagePromise(item) {
    return new Promise((resolve, reject) => {
      if (item.imageType == IMAGE_TYPES.QR_CODE) {
        let theImage = fabric.util.createImage();

        const testLoadedAndComplete = () => {
          if (theImage.complete) {
            resolve(item);
          } else {
            setTimeout(testLoadedAndComplete, 10);
          }
        };

        theImage.onload = testLoadedAndComplete;
        item.img = theImage;
        item.type = 'img';
        theImage.src = item.url;
      } else {
        if (GraphicItemUtils.imageCache[item.id] != undefined) {
          item.img = GraphicItemUtils.imageCache[item.id];
          item.fromCache = true;
          resolve(item);
        } else {
          if (item.url) {
            FabricUtils.loadImage(
              item.url,
              function (htmlImage) {
                if (!htmlImage) {
                  let retryUrl = item.url;
                  if (item.fallbackUrl) {
                    retryUrl = item.fallbackUrl;

                    // https://blurb-books.atlassian.net/browse/BWO-477
                    console.log(
                      [
                        'Retrying image with fallbackUrl.',
                        `Project PK: ${window.projectPK ?? null}, Image ID: ${item.id}, Width: ${item.originalImageWidth}, Height: ${item.originalImageHeight}.`,
                        'See https://blurb-books.atlassian.net/browse/BWO-477',
                      ].join(' '),
                    );

                    // For CloudWatch log filter
                    if (
                      item.originalImageWidth > 4096 ||
                      item.originalImageHeight > 4096
                    ) {
                      console.log(
                        `ORIGINAL_IMAGE_FALLBACK_WARNING_PROJECT_PK_${projectPK}`,
                      );
                    } else {
                      console.log(
                        `ORIGINAL_IMAGE_FALLBACK_INFO_PROJECT_PK_${projectPK}`,
                      );
                    }
                  }
                  FabricUtils.loadImage(retryUrl, function (innerHtmlImage) {
                    if (!innerHtmlImage) {
                      console.error('failed retry for URL:', retryUrl);
                      reject(item);
                    } else {
                      item.img = innerHtmlImage;
                      resolve(item);
                    }
                  });
                  return;
                } else {
                  item.img = htmlImage;
                  resolve(item);
                }
              },
              this,
              'anonymous',
            );
          } else {
            console.error('no url for image', item);
            reject(item);
          }
        }
      }
    });
  }
  static loadImages(urls, images, callback) {
    if (urls.length < 1) {
      callback(images);
    }

    const loadJobs = urls.map((item) => this.loadImagePromise(item));
    Promise.all(loadJobs)
      .then((images) => {
        callback(images, null);
      })
      .catch(function (err) {
        // log that I have an error, return the entire array;
        callback(null, err);
      });
  }

  static createCornerOverLay(cornerImage, shadowImage, props, dim) {
    if (cornerImage == undefined) {
      return;
    }

    let cornerOverLay = createCanvas();
    cornerOverLay.id = 'corner';

    const topLeftShadow = this.cropPlusExport(
      shadowImage,
      0,
      0,
      shadowImage.width / 2,
      shadowImage.height / 2,
      dim,
    );
    const topRightShadow = this.cropPlusExport(
      shadowImage,
      shadowImage.width / 2,
      0,
      shadowImage.width / 2,
      shadowImage.height / 2,
      dim,
    );
    const bottomLeftShadow = this.cropPlusExport(
      shadowImage,
      0,
      shadowImage.height / 2,
      shadowImage.width / 2,
      shadowImage.height / 2,
      dim,
    );
    const bottomRightShadow = this.cropPlusExport(
      shadowImage,
      shadowImage.width / 2,
      shadowImage.height / 2,
      shadowImage.width / 2,
      shadowImage.height / 2,
      dim,
    );

    const topLeft = this.cropPlusExport(
      cornerImage,
      0,
      0,
      cornerImage.width / 2,
      cornerImage.height / 2,
      dim,
    );
    const topRight = this.cropPlusExport(
      cornerImage,
      cornerImage.width / 2,
      0,
      cornerImage.width / 2,
      cornerImage.height / 2,
      dim,
    );
    const bottomLeft = this.cropPlusExport(
      cornerImage,
      0,
      cornerImage.height / 2,
      cornerImage.width / 2,
      cornerImage.height / 2,
      dim,
    );
    const bottomRight = this.cropPlusExport(
      cornerImage,
      cornerImage.width / 2,
      cornerImage.height / 2,
      cornerImage.width / 2,
      cornerImage.height / 2,
      dim,
    );

    let targetHeight = props.height + topLeft.height / 2;
    let targetWidth = props.width + topLeft.width / 2;
    cornerOverLay.width = targetWidth;
    cornerOverLay.height = targetHeight;
    const context = cornerOverLay.getContext('2d');

    // for debug visual
    // context.fillStyle='#FF0000';
    // context.originX =  'center';
    // context.originY =  'center';
    // context.globalAlpha=0.1;
    // context.fillRect(0,0,targetWidth,targetHeight);
    // context.fillStyle='#0000FF';
    // context.globalAlpha=0.1;
    // context.fillRect(0,0,props.width,props.height);
    //
    // context.globalAlpha=1;

    context.drawImage(topLeftShadow, 0, 0);
    context.drawImage(topRightShadow, targetWidth - topRight.width, 0);
    context.drawImage(bottomLeftShadow, 0, targetHeight - bottomLeft.height);
    context.drawImage(
      bottomRightShadow,
      targetWidth - bottomRight.width,
      targetHeight - bottomRight.height,
    );

    context.drawImage(topLeft, 0, 0);
    context.drawImage(topRight, targetWidth - topRight.width, 0);
    context.drawImage(bottomLeft, 0, targetHeight - bottomLeft.height);
    context.drawImage(
      bottomRight,
      targetWidth - bottomRight.width,
      targetHeight - bottomRight.height,
    );

    let croppedImage = fabric.util.createImage(); //(typeof Image !== 'undefined') ? new Image() : new Canvas.Image();
    croppedImage.src = cornerOverLay.toDataURL();
    return new Promise((resolve, reject) => {
      if (!croppedImage.complete) {
        const waitForImageToBeComplete = () => {
          if (croppedImage.complete) {
            resolve(
              GraphicItemUtils.addFabricCorner(
                croppedImage,
                props,
                targetWidth,
                targetHeight,
                topLeft,
              ),
            );
          } else {
            setTimeout(waitForImageToBeComplete, 5);
          }
        };

        waitForImageToBeComplete();
      } else {
        resolve(
          GraphicItemUtils.addFabricCorner(
            croppedImage,
            props,
            targetWidth,
            targetHeight,
            topLeft,
          ),
        );
      }
    });
  }

  static addFabricCorner(
    croppedImage,
    props,
    targetWidth,
    targetHeight,
    topLeft,
  ) {
    let tmpImg = new fabric.Image();
    tmpImg.setElement(croppedImage);
    props.width = targetWidth;
    props.height = targetHeight;
    props.cornerWidthPadding = topLeft.width / 2;
    props.conerHeightPadding = topLeft.height / 2;

    tmpImg.set(props);
    return tmpImg;
  }

  static adjustImageToWell(pi, dim, obj, maintainAspectRatio = false) {
    let wellX = pi.get('positionX') * dim.ratioWidth;
    let wellY = pi.get('positionY') * dim.ratioWidth;
    let wellWidth = pi.get('width') * dim.ratioWidth;
    let wellHeight = pi.get('height') * dim.ratioWidth;
    let imageWidth = pi.get('image_width') * dim.ratioWidth;
    let imageHeight = pi.get('image_height') * dim.ratioWidth;
    let imageXOffset = pi.get('image_positionX') * dim.ratioWidth;
    let imageYOffset = pi.get('image_positionY') * dim.ratioWidth;

    var roundedWellRatio = (wellWidth / wellHeight).toFixed(5);
    var roundedObjectRatio = (obj.width / obj.height).toFixed(5);

    const diff = Math.abs(roundedWellRatio - roundedObjectRatio);

    if (maintainAspectRatio || diff < 0.01) {
      return this.resizeFromCorner(
        obj,
        wellWidth,
        wellHeight,
        imageXOffset,
        imageYOffset,
        imageWidth,
        imageHeight,
      );
    } else {
      return this.resizeFromEdge(
        obj,
        wellX,
        wellY,
        wellWidth,
        wellHeight,
        imageXOffset,
        imageYOffset,
        imageWidth,
        imageHeight,
      );
    }
  }

  static resizeFromCorner(
    obj,
    wellWidth,
    wellHeight,
    imageXOffset,
    imageYOffset,
    imageWidth,
    imageHeight,
  ) {
    const resizeXPercent = obj.width / wellWidth;
    const resizeYPercent = obj.height / wellHeight;

    imageXOffset = Math.min(imageXOffset * resizeXPercent, 0);
    imageYOffset = Math.min(imageYOffset * resizeYPercent, 0);
    imageWidth = imageWidth * resizeXPercent;
    imageHeight = imageHeight * resizeYPercent;

    return {
      x: imageXOffset,
      y: imageYOffset,
      width: imageWidth,
      height: imageHeight,
    };
  }

  static resizeFromEdge(
    obj,
    wellX,
    wellY,
    wellWidth,
    wellHeight,
    imageXOffset,
    imageYOffset,
    imageWidth,
    imageHeight,
  ) {
    if (Math.round(wellX) != Math.round(obj.left)) {
      //we're resizing from the left handle
      imageXOffset += wellX - obj.left;

      if (imageXOffset > 0) {
        imageXOffset = 0;
      }
    } else if (Math.round(wellWidth) != Math.round(obj.width)) {
      //this needs no adjustment until we hit the right edge.
      if (Math.round(obj.width) > Math.round(imageWidth + imageXOffset)) {
        const diff =
          Math.round(obj.width) - Math.round(imageWidth + imageXOffset);
        imageXOffset += diff;
      }
    }

    if (Math.round(wellY) != Math.round(obj.top)) {
      imageYOffset += wellY - obj.top;

      if (imageYOffset > 0) {
        imageYOffset = 0;
      }
    } else if (Math.round(wellHeight) != Math.round(obj.height)) {
      //this needs no adjustment until we hit the bottom edge.
      if (Math.round(obj.height) > Math.round(imageHeight + imageYOffset)) {
        const diff =
          Math.round(obj.height) - Math.round(imageHeight + imageYOffset);
        imageYOffset += diff;
      }
    }

    if (imageWidth < obj.width) {
      const ratio = imageHeight / imageWidth;
      const difference = imageWidth - obj.width;
      imageWidth = obj.width;
      imageHeight = imageWidth * ratio;
      //now adjust the positionY
      imageXOffset = 0;
      imageYOffset += difference / 2;
    }

    if (imageHeight < obj.height) {
      const ratio = imageWidth / imageHeight;
      const difference = imageHeight - obj.height;
      imageHeight = obj.height;
      imageWidth = imageHeight * ratio;
      //now adjust the positionY
      imageYOffset = 0;
      imageXOffset += difference / 2;
    }

    return {
      x: imageXOffset,
      y: imageYOffset,
      width: imageWidth,
      height: imageHeight,
    };
  }

  static isLowRes(graphicItemDimensions, image, resolutionFactor) {
    if (!resolutionFactor || !image) {
      //console.error('no resolutionFactor');
      return false;
    }

    if (
      image.get('serverImage') == '1' ||
      image.get('imageType') == PicabooConstants.IMAGE_TYPES.QR_CODE ||
      image.get('imageType') === PicabooConstants.IMAGE_TYPES.PORTRAIT
    ) {
      return false;
    }

    var targetWidth = image.get('width');
    var printedImageWidth =
      resolutionFactor.get('printedWidth') * graphicItemDimensions.width;

    if (GraphicItemUtils.isPortrait(image.toJS())) {
      targetWidth = image.get('height');
      printedImageWidth =
        resolutionFactor.get('printedWidth') * graphicItemDimensions.height;
    }

    const dpi = GraphicItemUtils.calculateDPI(printedImageWidth, targetWidth);

    return resolutionFactor.get('resolutionWarningDPI') > dpi;
  }

  static isPortrait(image) {
    return (
      image.orientation == PicabooConstants.ORIENTATION_PORTRAIT_LEFT ||
      image.orientation == PicabooConstants.ORIENTATION_PORTRAIT_RIGHT ||
      image.height > image.width
    );
  }

  static calculateDPI(printedWidth, originalWidth) {
    const calculatedWidth = originalWidth / printedWidth;
    return DPI * calculatedWidth;
  }

  static isOffPage(pi, pageRatio, ratioBase) {
    const pageRect = {
      x: 0,
      y: 0,
      width: ratioBase || 1,
      height: pageRatio,
      rotation: 0,
    };

    const piRect = {
      x: pi.get('positionX'),
      y: pi.get('positionY'),
      width: pi.get('width'),
      height: pi.get('height'),
      rotation: pi.get('rotation'),
    };

    if (!intersects(pageRect, getContainingRectangle(piRect))) {
      return true;
    }
    return false;
  }

  static removeFromCache(id) {
    delete GraphicItemUtils.imageCache[id];
  }

  static getWarningIcon(wellWidth, wellHeight) {
    const warningIcon = new fabric.IText('\uf071', {
      fill: '#E37131',
      left: -wellWidth / 2 + 14,
      top: -wellHeight / 2 + 13,
      originX: 'left',
      originY: 'top',
      fontFamily: 'FontAwesome',
      fontSize: 15,
      picabooType: 'warning',
      adjust: {
        left: 14,
        top: 13,
      },
    });
    return warningIcon;
  }

  static getWarningCircle(wellWidth, wellHeight) {
    const warningCircle = new fabric.Circle({
      originX: 'left',
      originY: 'top',
      left: -wellWidth / 2 + 8,
      top: -wellHeight / 2 + 8,
      radius: 14,
      type: 'circle',
      fill: '#D9D9D9',
      picabooType: 'warning',
      adjust: {
        left: 8,
        top: 8,
      },
    });
    return warningCircle;
  }
}

GraphicItemUtils.imageCache = [];
GraphicItemUtils.backgroundDimensionCache = [];
